/**
 * QR-2, веб-срез В1: перевод между НАШИМ списком устройств и телами запросов
 * криптомашины (KeysQuery, KeysClaim).
 *
 * Почему отдельный модуль, а не строки внутри olmClient. Машина живёт в WASM:
 * `new UserId(...)` до `initAsync()` бросает «used before it was initialized»,
 * то есть в unit-тесте её не потрогать вовсе. А ошибаться тут дорого и ТИХО:
 * перепутанный did или пропущенный принципал не падают — они дают конверт,
 * который получатель не расшифрует, и увидим мы это по «не расшифровано» у
 * человека. Поэтому весь перевод — здесь, чистыми функциями без единого
 * импорта, а в olmClient остаётся только проводка к машине.
 *
 * Слова «комната» тут нет намеренно (§1.1 дизайна): модуль знает про
 * устройства и принципалов, а не про то, кому мы пишем.
 *
 * 🔴 matrix id строится ТОЛЬКО функцией, переданной снаружи (`matrixIdOf`).
 * Своя копия конвенции `@e<id>/@o<id>/@m<id>:total.local` была бы четвёртой в
 * проекте (olmClient, эталон, Android) — и разошлась бы первой.
 */

/** Ключ принципала в состоянии экрана и в пинах доверия: тип + id. */
export function principalKey(principalType, principalId) {
  return `${principalType}:${principalId}`;
}

/**
 * Ответ сервера `GET /msg/rooms/{id}/devices` -> внутренняя форма.
 * Своего устройства в ответе нет — сервер исключает его сам.
 */
export function toDeviceEntries(devices = []) {
  return devices.map((d) => ({
    deviceId: d.device_id,
    principalType: d.principal_type,
    principalId: d.principal_id,
    did: d.did,
    deviceKeys: d.device_keys,
  }));
}

/** Уникальные matrix id принципалов списка, в порядке первого появления. */
export function matrixIdsOf(entries, matrixIdOf) {
  const seen = [];
  for (const e of entries) {
    const uid = matrixIdOf(e.principalType, e.principalId);
    if (!seen.includes(uid)) seen.push(uid);
  }
  return seen;
}

/**
 * Ответ на KeysQuery машины: identity-бандлы тех, о ком она спросила.
 *
 * 🔴 Отвечаем ТОЛЬКО про спрошенных и только известными бандлами. Ответ «за
 * всех, кого знаю» машина примет, но лишний принципал в ответе — это заявка,
 * что его список устройств полный, а он у нас неполный по построению (в
 * ответе сервера нет своего устройства).
 *
 * @param queryBody разобранное тело req.body машины
 * @param entries наши устройства
 * @param self {matrixId, did, deviceKeys} — своё устройство, если машина
 *        спросила о себе; deviceKeys=null (resume из стора) = своего тела в
 *        ответе нет, но свои ДРУГИЕ устройства из списка отдаём (МУ-1 А)
 */
export function keysQueryAnswer(queryBody, entries, self, matrixIdOf) {
  const queried = queryBody?.device_keys ?? {};
  const known = new Map();
  for (const e of entries) {
    const uid = matrixIdOf(e.principalType, e.principalId);
    if (!known.has(uid)) known.set(uid, {});
    known.get(uid)[e.did] = e.deviceKeys;
  }
  const deviceKeys = {};
  for (const uid of Object.keys(queried)) {
    if (self && uid === self.matrixId) {
      // МУ-1, срез А: про СЕБЯ отвечаем всем, что знаем, — своими ДРУГИМИ
      // устройствами из списка (сервер исключает лишь запрашивающее) и своим
      // телом, если оно есть. 🔴 На resume тела нет (ключи уже выгружены), и
      // молчать тогда про себя целиком нельзя (ревью Codex р.1, High): машина
      // так и не узнала бы телефон, и отправка в комнату с компьютера упала бы
      // на `getDevice(..., did) === null`. Отсутствие СОБСТВЕННОГО устройства в
      // ответе машину не ломает: своё текущее устройство она не удаляет.
      // Не знаем ничего — молчим, а не врём пустым списком.
      const mine = { ...(known.get(uid) ?? {}) };
      if (self.deviceKeys) mine[self.did] = self.deviceKeys;
      if (Object.keys(mine).length) deviceKeys[uid] = mine;
    } else if (known.has(uid)) {
      deviceKeys[uid] = known.get(uid);
    }
  }
  return { device_keys: deviceKeys, failures: {} };
}

/**
 * Чьи one-time ключи просит машина — в НАШИХ device_id.
 *
 * Это и есть ответ на вопрос «кому не хватает Olm-сессии»: спрашивает машина,
 * а не мы гадаем по своему кэшу. Устройство, которого машина не просила,
 * в claim не идёт — иначе каждый вызов жёг бы чужой пул ключей впустую.
 */
export function claimTargets(claimBody, entries, matrixIdOf) {
  const wanted = claimBody?.one_time_keys ?? {};
  const ids = [];
  for (const e of entries) {
    const forUser = wanted[matrixIdOf(e.principalType, e.principalId)];
    if (forUser && Object.prototype.hasOwnProperty.call(forUser, e.did)) {
      if (!ids.includes(e.deviceId)) ids.push(e.deviceId);
    }
  }
  return ids;
}

/**
 * Ответ на KeysClaim машины из ответа сервера `POST .../claim`.
 *
 * 🔴 Устройства без ключа (`one_time_key === null`, пул пуст и fallback'а нет)
 * в ответ НЕ попадают: сессию им не установить, и врать об этом машине нельзя.
 * Кто именно остался без сессии — считает `blockedDeviceIds`, и решение по
 * ним принимает контроллер (§4.1 дизайна: не отправлять вовсе).
 */
export function claimAnswer(keys = [], entries, matrixIdOf) {
  const byId = new Map(entries.map((e) => [e.deviceId, e]));
  const oneTimeKeys = {};
  for (const k of keys) {
    const e = byId.get(k.device_id);
    if (!e || !k.one_time_key_id || !k.one_time_key) continue;
    const uid = matrixIdOf(e.principalType, e.principalId);
    if (!oneTimeKeys[uid]) oneTimeKeys[uid] = {};
    oneTimeKeys[uid][e.did] = { [k.one_time_key_id]: k.one_time_key };
  }
  return { one_time_keys: oneTimeKeys, failures: {} };
}

/**
 * Устройства, которым сервер ключа не дал: пул пуст И fallback не задан.
 * Видимая деградация §2.2 — человек на той стороне обязан открыть чат, чтобы
 * долить пул. Сервер сам это состояние НЕ лечит.
 */
export function blockedDeviceIds(keys = []) {
  return keys.filter((k) => !k.one_time_key).map((k) => k.device_id);
}
